<template>
    <SectionBase :section="section">
        <div class="tabs-section-content" :style="sectionContentStyle">

            <TabsSelector :tabs="tabs" @tab-change="activeTabIndex = $event" :section-bg-color="section?.backgroundSettings?.backgroundColor" />

            <div class="tabs-stage-shell">
                <Transition name="tabs-content-fade">
                    <div :key="activeTabKey" class="tabs-stage" :class="[mockupTypeClass, hasTextClass, hasMediaClass, textOnlyClass]" :style="stageStyle">
                        <template v-for="module in activeModules" :key="module.moduleId">
                            <div
                                v-if="module.type === SectionModuleTypeEnum.MEDIA && activeTab?.media"
                                class="tabs-module"
                                :style="moduleStyle(module)"
                            >
                                <GetMediaContent :section="activeTabMediaSection" />
                                <GetMediaCaption :caption="activeTab.media.caption" />
                            </div>
                            <div
                                v-if="module.type === SectionModuleTypeEnum.TEXT && activeTab?.text"
                                class="tabs-module text"
                                :style="moduleStyle(module)"
                            >
                                <SectionText :section-text="activeTab.text" />
                            </div>
                        </template>
                    </div>
                </Transition>
            </div>
        </div>
    </SectionBase>
</template>

<script>
import SectionBase from "@/scripts/view/portfolio/sections/SectionBase.vue";
import SectionText from "@/scripts/view/portfolio/sections/_helper/GetSectionText.vue";
import GetMediaContent from "@/scripts/view/portfolio/sections/_helper/GetMediaContent.vue";
import GetMediaCaption from "@/scripts/view/portfolio/sections/_helper/GetMediaCaption.vue";
import {SectionModuleTypeEnum} from "@/scripts/common/enums.js";
import {mapGetters} from "vuex";
import TabsSelector from "@/scripts/sections/common/TabsSelector.vue";
import {
    getTabsContentModules,
    getTabsInternalGridColumns,
    getTabsSectionContentStyle,
    hasTabsModule,
    isTabsTextOnly
} from "@/scripts/common/utilities/tabsLayout.js";

export default {
    name: "Tabs",
    components: {
        TabsSelector,
        GetMediaCaption,
        GetMediaContent,
        SectionText,
        SectionBase
    },
    props: {
        section: Object
    },
    data() {
        return {
            activeTabIndex: 0,
            SectionModuleTypeEnum
        };
    },
    computed: {
        ...mapGetters("design", ["colorPalette"]),
        tabs() {
            return Array.isArray(this.section?.tabs) ? this.section.tabs : [];
        },
        activeTab() {
            return this.tabs[this.activeTabIndex] || this.tabs[0] || null;
        },
        activeTabKey() {
            return this.activeTab?.tabId || this.activeTabIndex;
        },
        activeModules() {
            return getTabsContentModules(this.activeTab?.layoutSettings?.modules);
        },
        sectionContentStyle() {
            return getTabsSectionContentStyle(
                this.activeTab,
                this.activeTab?.layoutSettings?.modules,
                this.selectedImageAspectRatio
            );
        },
        activeTabMediaSection() {
            return {
                ...this.section,
                mediaAspectRatio: this.selectedImageAspectRatio,
                media: this.activeTab?.media || null
            };
        },
        hasMedia() {
            return hasTabsModule(this.activeTab?.layoutSettings?.modules, SectionModuleTypeEnum.MEDIA);
        },
        hasMediaClass() {
            return this.hasMedia ? "has-media" : "no-media";
        },
        hasTextClass() {
            return hasTabsModule(this.activeTab?.layoutSettings?.modules, SectionModuleTypeEnum.TEXT) ? "has-text" : "no-text";
        },
        hasTextOnly() {
            return isTabsTextOnly(this.activeTab?.layoutSettings?.modules);
        },
        hasPlainImageOnly() {
            return this.hasMedia
                && this.activeModules.length === 1
                && !this.activeTab?.media?.mockupSettings;
        },
        textOnlyClass() {
            return this.hasTextOnly ? "text-only" : null;
        },
        mockupTypeClass() {
            return this.hasMedia ? this.activeTab?.media?.mockupSettings?.deviceType?.toLowerCase() || "no-mockup" : null;
        },
        tabsInternalGridColumns() {
            return getTabsInternalGridColumns(this.activeTab, this.activeTab?.layoutSettings?.modules);
        },
        selectedImageAspectRatio() {
            return this.section?.mediaAspectRatio || "16_9";
        },
        stageStyle() {
            const style = {
                "--tabs-stage-cols": this.tabsInternalGridColumns,
                "--tabs-frame-radius": `${this.section?.cornerRadiusPx ?? 0}px`
            };
            const frameBackground = this.resolveFrameBackgroundColor();
            if (frameBackground) {
                style["--tabs-frame-bg"] = frameBackground;
            }
            if (this.hasTextOnly || this.hasPlainImageOnly) return style;

            return {
                ...style,
                backgroundColor: frameBackground,
                borderRadius: `${this.section?.cornerRadiusPx ?? 0}px`
            };
        }
    },
    methods: {
        calculateMediaAspectRatio(module) {
            const media = this.activeTab?.media;
            if (module.type !== SectionModuleTypeEnum.MEDIA || !media || media?.mockupSettings) return null;

            if (!module.gridSettings?.mediaHeight) return `${media?.width} / ${media?.height}`;

            const savedAr = module.gridSettings?.mediaAspectRatio;
            if (savedAr) {
                return `${savedAr[0]} / ${savedAr[1]}`;
            }

            return null;
        },
        moduleStyle(module) {
            const grid = module?.gridSettings || {};
            const style = {
                gridColumn: `${grid.columnStart || 1} / ${grid.columnEnd || 13}`
            };

            if (module.type !== SectionModuleTypeEnum.MEDIA) return style;

            return {
                ...style,
                "--mediaHeight": !this.calculateMediaAspectRatio(module) && grid.mediaHeight ? `${grid.mediaHeight}px` : null,
                "--mediaAspectRatio": this.calculateMediaAspectRatio(module)
            };
        },
        resolveFrameBackgroundColor() {
            const colorData = this.section?.stageBackgroundColor;
            if (!colorData) return null;
            if (Number.isFinite(colorData.paletteColorIndex)) {
                return this.colorPalette?.colors?.[colorData.paletteColorIndex] || null;
            }
            return colorData.color || null;
        }
    }
}
</script>

<style lang="scss" scoped>
.tabs-stage {
    &.text-only {
        .tabs-module.text {
            position: relative;
        }
    }
}
</style>
